import type { CpuState, Location, Symbols } from "./symbols.ts";

const ADDRESS = /0x[0-9a-f]{8}/gi;
const ANSI_ESCAPE = /\x1b\[[0-9;]*[A-Za-z]/g;
const REGISTER_DUMP = /Core\s*\d+ register dump:/;
const REGISTER = /([A-Z_0-9/-]+)\s*:\s*0x([0-9a-f]+)/gi;
const STACK_LINE = /^([0-9a-f]{8}):((?:\s+0x[0-9a-f]{8})+)\s*$/i;
const ELF_SHA256 = /ELF file SHA256:\s*([0-9a-f]+)/i;

/** A line longer than this is passed through undecoded. */
const LONGEST_LINE = 4096;

/** The panic handler's names for the RISC-V registers, in register number order. */
const RISCV_REGISTERS = [
    "X0",
    "RA",
    "SP",
    "GP",
    "TP",
    "T0",
    "T1",
    "T2",
    "S0/FP",
    "S1",
    "A0",
    "A1",
    "A2",
    "A3",
    "A4",
    "A5",
    "A6",
    "A7",
    "S2",
    "S3",
    "S4",
    "S5",
    "S6",
    "S7",
    "S8",
    "S9",
    "S10",
    "S11",
    "T3",
    "T4",
    "T5",
    "T6",
];

const PREFIX = "---";
const YELLOW = "\x1b[33m";
const GREEN = "\x1b[32m";
const RED = "\x1b[31m";
const RESET = "\x1b[0m";

type Reading =
    | { kind: "idle" }
    | { kind: "registers"; registers: Map<string, number> }
    | {
          kind: "stack";
          registers: Map<string, number>;
          base: number;
          words: number[];
          /** The stack dump's lines, shown only if no backtrace comes out of them. */
          held: Uint8Array[];
      };

type Piece = Uint8Array | string;

/**
 * Decodes the console output of a board as esp-idf-monitor does: after each
 * line with code addresses in it, their functions and source lines; in place
 * of a RISC-V panic's stack dump, the backtrace unwound from it.
 *
 * The output is the input bytes with the decoded text inserted after the line
 * it belongs to. A stack dump is held back until it ends, and is passed on
 * unchanged if it cannot be unwound.
 */
export class SerialDecoder {
    readonly #symbols: Symbols | undefined;
    readonly #sha256: string | undefined;
    /** The current line's text so far, for decoding once it is complete. */
    #line: Uint8Array[] = [];
    #lineBytes = 0;
    /** The current line's bytes that have not been passed on yet. */
    #unsent: Uint8Array[] = [];
    #reading: Reading = { kind: "idle" };
    #warnedSha = false;

    /** `sha256` is the ELF file's, in hex, to tell whether the board runs it. */
    constructor(symbols: Symbols | undefined, sha256?: string) {
        this.#symbols = symbols;
        this.#sha256 = sha256;
    }

    push(bytes: Uint8Array): Piece[] {
        const out: Piece[] = [];
        let from = 0;
        for (let i = bytes.indexOf(0x0a); i >= 0; i = bytes.indexOf(0x0a, from)) {
            this.#keep(bytes.subarray(from, i));
            const raw = concat([...this.#unsent, bytes.subarray(from, i + 1)]);
            this.#unsent = [];
            out.push(...this.#complete(raw));
            from = i + 1;
        }
        if (from < bytes.length) {
            const rest = bytes.subarray(from);
            this.#keep(rest);
            // Inside a RISC-V panic a line is only passed on whole, once it is known
            // whether it belongs to the stack dump.
            if (this.#reading.kind === "idle") out.push(rest);
            else this.#unsent.push(rest.slice());
        }
        return out;
    }

    #keep(bytes: Uint8Array): void {
        if (this.#lineBytes > LONGEST_LINE) return;
        this.#line.push(bytes.slice());
        this.#lineBytes += bytes.length;
    }

    /** What to pass on for a line that just ended; `raw` is its bytes not passed on yet. */
    #complete(raw: Uint8Array): Piece[] {
        const tooLong = this.#lineBytes > LONGEST_LINE;
        const bytes = concat(this.#line);
        this.#line = [];
        this.#lineBytes = 0;
        const symbols = this.#symbols;
        if (tooLong || symbols === undefined) return [raw];
        const line = new TextDecoder().decode(bytes).replace(ANSI_ESCAPE, "").replace(/\r/g, "");
        const out: Piece[] = [];
        if (symbols.arch === "riscv" && this.#follow(line, raw, symbols, out)) return out;
        out.push(raw);
        const decoded = addressLines(line, symbols) + this.#checkSha(line);
        if (decoded !== "") out.push(decoded);
        return out;
    }

    /**
     * Follows a RISC-V panic: the register dump, then the stack memory up to
     * the first line that is not part of it. True if the line was held back.
     */
    #follow(line: string, raw: Uint8Array, symbols: Symbols, out: Piece[]): boolean {
        const reading = this.#reading;
        if (REGISTER_DUMP.test(line)) {
            this.#reading = { kind: "registers", registers: new Map() };
            return false;
        }
        if (reading.kind === "registers") {
            if (line.includes("Stack memory:")) {
                this.#reading = { ...reading, kind: "stack", base: -1, words: [], held: [raw] };
                return true;
            }
            for (const match of line.matchAll(REGISTER)) {
                reading.registers.set(match[1]!.toUpperCase(), parseInt(match[2]!, 16));
            }
            return false;
        }
        if (reading.kind !== "stack") return false;
        const match = STACK_LINE.exec(line.trim());
        if (match !== null) {
            const address = parseInt(match[1]!, 16);
            if (reading.base < 0) reading.base = address;
            if (address === reading.base + reading.words.length * 4) {
                for (const word of match[2]!.trim().split(/\s+/)) reading.words.push(parseInt(word, 16));
                reading.held.push(raw);
                return true;
            }
        }
        this.#reading = { kind: "idle" };
        const decoded = backtrace(symbols, reading.registers, reading.base, reading.words);
        if (decoded === "") out.push(...reading.held);
        else out.push(decoded);
        return false;
    }

    #checkSha(line: string): string {
        const match = ELF_SHA256.exec(line);
        if (match === null || this.#sha256 === undefined || this.#warnedSha) return "";
        if (this.#sha256.startsWith(match[1]!.toLowerCase())) return "";
        this.#warnedSha = true;
        return (
            `${RED}${PREFIX} Warning: the board runs a different firmware from the ELF file in the` +
            ` build directory, so the decoded addresses may be wrong.${RESET}\n`
        );
    }
}

function addressLines(line: string, symbols: Symbols): string {
    let out = "";
    for (const match of line.matchAll(ADDRESS)) {
        const address = parseInt(match[0], 16);
        const locations = symbols.locate(address);
        if (locations.length === 0) continue;
        out += `${PREFIX} ${match[0].toLowerCase()}: ${describe(locations[0]!)}\n`;
        for (const location of locations.slice(1)) out += `${PREFIX} (inlined by) ${describe(location)}\n`;
    }
    return out;
}

function backtrace(symbols: Symbols, named: Map<string, number>, base: number, words: number[]): string {
    const pc = named.get("MEPC");
    if (pc === undefined || base < 0) return "";
    const stack = new Uint8Array(words.length * 4);
    const view = new DataView(stack.buffer);
    words.forEach((word, i) => view.setUint32(i * 4, word, true));
    const state: CpuState = {
        registers: RISCV_REGISTERS.map((name) => (name === "X0" ? 0 : named.get(name))),
        pc,
        stackBase: base,
        stack,
    };
    const frames = symbols.unwind(state);
    if (frames.length === 0) return "";
    let out = `${PREFIX} Backtrace:\n`;
    let number = 0;
    for (const frame of frames) {
        const pcText = `0x${frame.pc.toString(16).padStart(8, "0")}`;
        if (frame.locations.length === 0) {
            out += `${PREFIX} #${number++}  ${pcText} in ??\n`;
            continue;
        }
        frame.locations.forEach((location, i) => {
            const at = i === 0 ? `${pcText} in ` : "";
            out += `${PREFIX} #${number++}  ${at}${describe(location)}\n`;
        });
    }
    return out;
}

function describe(location: Location): string {
    const func = `${YELLOW}${location.func}${RESET}`;
    const file = location.file ?? "??";
    const line = location.line === undefined ? "?" : String(location.line);
    return `${func} at ${GREEN}${file}${RESET}:${line}`;
}

function concat(parts: Uint8Array[]): Uint8Array {
    if (parts.length === 1) return parts[0]!;
    const out = new Uint8Array(parts.reduce((n, part) => n + part.length, 0));
    let at = 0;
    for (const part of parts) {
        out.set(part, at);
        at += part.length;
    }
    return out;
}
